<template>
  <div
    class="crm-card bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
    :draggable="true"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    @click="$emit('click', card)"
  >
    <!-- Priority Badge -->
    <div class="flex items-start justify-between mb-2">
      <span
        v-if="card.priority && card.priority !== 'low'"
        class="text-xs px-2 py-0.5 rounded-full font-medium"
        :class="priorityClass"
      >
        {{ priorityLabel }}
      </span>
      <div class="ml-auto flex items-center gap-1">
        <button
          class="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600"
          @click.stop="$emit('edit', card)"
        >
          <span class="i-lucide-pencil size-3" />
        </button>
        <button
          class="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-500"
          @click.stop="$emit('delete', card)"
        >
          <span class="i-lucide-trash-2 size-3" />
        </button>
      </div>
    </div>

    <!-- Title -->
    <h4 class="text-sm font-medium text-slate-800 dark:text-slate-100 mb-2 leading-snug">
      {{ card.title }}
    </h4>

    <!-- Contact -->
    <div v-if="card.contact" class="flex items-center gap-2 mb-2">
      <img
        v-if="card.contact.thumbnail"
        :src="card.contact.thumbnail"
        class="size-5 rounded-full object-cover"
        :alt="card.contact.name"
      />
      <span v-else class="size-5 rounded-full bg-woot-500 flex items-center justify-center text-white text-xs font-bold">
        {{ card.contact.name?.charAt(0)?.toUpperCase() }}
      </span>
      <span class="text-xs text-slate-600 dark:text-slate-400 truncate">{{ card.contact.name }}</span>
    </div>

    <!-- Conversation link -->
    <div v-if="card.conversation" class="flex items-center gap-1 mb-2">
      <span class="i-lucide-message-circle size-3 text-slate-400" />
      <span class="text-xs text-woot-500 hover:underline cursor-pointer" @click.stop="openConversation">
        #{{ card.conversation.display_id }}
      </span>
      <span
        class="text-xs px-1.5 py-0.5 rounded-full ml-1"
        :class="conversationStatusClass"
      >
        {{ conversationStatusLabel }}
      </span>
    </div>

    <!-- Footer -->
    <div class="flex items-center justify-between mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
      <!-- Deal value -->
      <span v-if="card.deal_value > 0" class="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
        {{ formatCurrency(card.deal_value, card.currency) }}
      </span>
      <span v-else class="text-xs text-slate-400">Sem valor</span>

      <!-- Assignee -->
      <div v-if="card.assignee" class="flex items-center gap-1">
        <img
          v-if="card.assignee.thumbnail"
          :src="card.assignee.thumbnail"
          class="size-5 rounded-full object-cover"
          :alt="card.assignee.name"
          :title="card.assignee.name"
        />
        <span v-else class="size-5 rounded-full bg-slate-400 flex items-center justify-center text-white text-xs font-bold" :title="card.assignee.name">
          {{ card.assignee.name?.charAt(0)?.toUpperCase() }}
        </span>
      </div>
    </div>

    <!-- Due date -->
    <div v-if="card.due_date" class="mt-1 flex items-center gap-1" :class="dueDateClass">
      <span class="i-lucide-calendar size-3" />
      <span class="text-xs">{{ formatDate(card.due_date) }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps({
  card: { type: Object, required: true },
});

const emit = defineEmits(['click', 'edit', 'delete', 'dragstart', 'dragend']);
const router = useRouter();

const priorityClass = computed(() => ({
  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400': props.card.priority === 'urgent',
  'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400': props.card.priority === 'high',
  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400': props.card.priority === 'medium',
}));

const priorityLabel = computed(() => ({
  urgent: 'Urgente',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
}[props.card.priority] || ''));

const conversationStatusClass = computed(() => ({
  'bg-green-100 text-green-700': props.card.conversation?.status === 'open',
  'bg-slate-100 text-slate-600': props.card.conversation?.status === 'resolved',
  'bg-yellow-100 text-yellow-700': props.card.conversation?.status === 'pending',
  'bg-blue-100 text-blue-700': props.card.conversation?.status === 'snoozed',
}));

const conversationStatusLabel = computed(() => ({
  open: 'Aberta',
  resolved: 'Resolvida',
  pending: 'Pendente',
  snoozed: 'Adiada',
}[props.card.conversation?.status] || ''));

const dueDateClass = computed(() => {
  if (!props.card.due_date) return '';
  const due = new Date(props.card.due_date);
  const now = new Date();
  if (due < now) return 'text-red-500';
  const diff = due - now;
  if (diff < 86400000) return 'text-orange-500';
  return 'text-slate-400';
});

function formatCurrency(value, currency = 'BRL') {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function openConversation() {
  if (props.card.conversation) {
    const accountId = window.location.pathname.split('/')[3];
    router.push(`/app/accounts/${accountId}/conversations/${props.card.conversation.id}`);
  }
}

function onDragStart(e) {
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('cardId', props.card.id);
  e.dataTransfer.setData('fromStageId', props.card.stage_id);
  emit('dragstart', props.card);
}

function onDragEnd() {
  emit('dragend', props.card);
}
</script>
